// src/pages/MantenedorMenu.js
import React, {useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {Link} from 'react-router-dom'

const API_BASE = 'https://sistemarab.com/CasinoApp'

function ymNow() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function daysInMonth(year, month1to12) {
  return new Date(year, month1to12, 0).getDate()
}

const TYPE_LABEL = {
  FONDO_1: 'Fondo 1',
  FONDO_2: 'Fondo 2',
  HIPOCALORICO: 'Hipocalórico',
  VEGETARIANO: 'Vegetariano',
  OTRO: 'Otro',
}

function formatCLP(n) {
  const v = Number(n || 0)
  return v.toLocaleString('es-CL', {style: 'currency', currency: 'CLP', maximumFractionDigits: 0})
}

function optLabel(p) {
  // p trae: nombre, costo_elaboracion, precio_venta, margen
  const costo = Number(p.costo_elaboracion || 0)
  const precio = Number(p.precio_venta || 0)
  const margen = Number(p.margen ?? (precio - costo))
  const meta = ` | Costo: ${formatCLP(costo)} | Precio: ${formatCLP(precio)} | Margen: ${formatCLP(margen)}`
  return `${p.nombre}${meta}`
}

export default function MantenedorMenu() {
  const [month, setMonth] = useState(ymNow())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const [menuMap, setMenuMap] = useState({})

  const [platosF1, setPlatosF1] = useState([])
  const [platosF2, setPlatosF2] = useState([])
  const [platosHipo, setPlatosHipo] = useState([])
  const [platosVeg, setPlatosVeg] = useState([])
  const [platosOtro, setPlatosOtro] = useState([])

  const [show, setShow] = useState(false)
  const [fecha, setFecha] = useState('')
  const [f1, setF1] = useState('')
  const [f2, setF2] = useState('')
  const [hipo, setHipo] = useState('')
  const [veg, setVeg] = useState('')
  const [otro, setOtro] = useState('')
  const [nota, setNota] = useState('')

  // Crear plato con costo y precio
  const [newTipo, setNewTipo] = useState('FONDO_1')
  const [newPlato, setNewPlato] = useState('')
  const [newCosto, setNewCosto] = useState('')
  const [newPrecio, setNewPrecio] = useState('')

  const year = Number(month.split('-')[0])
  const monthNum = Number(month.split('-')[1])

  const gridCells = useMemo(() => {
    const total = daysInMonth(year, monthNum)
    const first = new Date(year, monthNum - 1, 1)
    const weekday = first.getDay()
    const mondayIndex = (weekday + 6) % 7

    const cells = []
    for (let i = 0; i < mondayIndex; i++) cells.push(null)
    for (let d = 1; d <= total; d++) cells.push(new Date(year, monthNum - 1, d))
    while (cells.length % 7 !== 0) cells.push(null)
    return cells
  }, [year, monthNum])

  async function fetchPlatosTipo(tipo) {
    const res = await axios.get(`${API_BASE}/getPlatos.php`, {params: {tipo}})
    const items = res.data?.items || []
    return items.filter((p) => Number(p.activo) === 1)
  }

  async function loadCatalogos() {
    const [a, b, c, d, e] = await Promise.all([
      fetchPlatosTipo('FONDO_1'),
      fetchPlatosTipo('FONDO_2'),
      fetchPlatosTipo('HIPOCALORICO'),
      fetchPlatosTipo('VEGETARIANO'),
      fetchPlatosTipo('OTRO'),
    ])
    setPlatosF1(a)
    setPlatosF2(b)
    setPlatosHipo(c)
    setPlatosVeg(d)
    setPlatosOtro(e)
  }

  async function loadMenuMes() {
    const mRes = await axios.get(`${API_BASE}/getMenuMes.php`, {params: {ym: month}})
    const items = mRes.data?.items || []
    const map = {}
    items.forEach((it) => (map[it.fecha] = it))
    setMenuMap(map)
  }

  async function loadAll() {
    setErr('')
    setLoading(true)
    try {
      await Promise.all([loadCatalogos(), loadMenuMes()])
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Error cargando datos')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month])

  function openDay(dayYMD) {
    const it = menuMap[dayYMD]
    setFecha(dayYMD)
    setF1(it?.id_fondo1 ?? '')
    setF2(it?.id_fondo2 ?? '')
    setHipo(it?.id_hipocalorico ?? '')
    setVeg(it?.id_vegetariano ?? '')
    setOtro(it?.id_otro ?? '')
    setNota(it?.nota ?? '')
    setNewTipo('FONDO_1')
    setNewPlato('')
    setNewCosto('')
    setNewPrecio('')
    setShow(true)
  }

  async function saveDay() {
    setErr('')
    setSaving(true)
    try {
      await axios.post(`${API_BASE}/saveMenuDia.php`, {
        fecha,
        id_fondo1: f1 === '' ? null : Number(f1),
        id_fondo2: f2 === '' ? null : Number(f2),
        id_hipocalorico: hipo === '' ? null : Number(hipo),
        id_vegetariano: veg === '' ? null : Number(veg),
        id_otro: otro === '' ? null : Number(otro),
        nota: nota.trim() === '' ? null : nota.trim(),
      })
      await loadMenuMes()
      setShow(false)
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Error guardando menú del día')
    } finally {
      setSaving(false)
    }
  }

  async function addPlatoQuick() {
    setErr('')
    const nombre = newPlato.trim()
    if (nombre.length < 3) {
      setErr('El plato debe tener al menos 3 caracteres.')
      return
    }

    const costo = newCosto === '' ? 0 : Number(String(newCosto).replace(',', '.'))
    const precio = newPrecio === '' ? 0 : Number(String(newPrecio).replace(',', '.'))

    if (!Number.isFinite(costo) || costo < 0) {
      setErr('Costo inválido (debe ser 0 o mayor).')
      return
    }
    if (!Number.isFinite(precio) || precio < 0) {
      setErr('Precio inválido (debe ser 0 o mayor).')
      return
    }

    setSaving(true)
    try {
      await axios.post(`${API_BASE}/addPlato.php`, {
        nombre,
        tipo: newTipo,
        costo_elaboracion: costo,
        precio_venta: precio,
      })

      // recargar solo el tipo agregado
      const updated = await fetchPlatosTipo(newTipo)
      if (newTipo === 'FONDO_1') setPlatosF1(updated)
      if (newTipo === 'FONDO_2') setPlatosF2(updated)
      if (newTipo === 'HIPOCALORICO') setPlatosHipo(updated)
      if (newTipo === 'VEGETARIANO') setPlatosVeg(updated)
      if (newTipo === 'OTRO') setPlatosOtro(updated)

      setNewPlato('')
      setNewCosto('')
      setNewPrecio('')
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Error creando plato')
    } finally {
      setSaving(false)
    }
  }

  function summary(dayYMD) {
    const it = menuMap[dayYMD]
    if (!it) return null
    const parts = []
    if (it.fondo1_nombre) parts.push(`F1: ${it.fondo1_nombre}`)
    if (it.fondo2_nombre) parts.push(`F2: ${it.fondo2_nombre}`)
    if (it.hipocalorico_nombre) parts.push(`H: ${it.hipocalorico_nombre}`)
    if (it.vegetariano_nombre) parts.push(`V: ${it.vegetariano_nombre}`)
    if (it.otro_nombre) parts.push(`O: ${it.otro_nombre}`)
    return parts.join(' • ')
  }

  function dayMargin(dayYMD) {
    const it = menuMap[dayYMD]
    if (!it) return 0
    const vals = [
      Number(it.fondo1_margen || 0),
      Number(it.fondo2_margen || 0),
      Number(it.hipocalorico_margen || 0),
      Number(it.vegetariano_margen || 0),
      Number(it.otro_margen || 0),
    ]
    return vals.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0)
  }

  function SelectPlato({value, onChange, items}) {
    return (
      <div className="select is-fullwidth">
        <select value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">-- Seleccionar --</option>
          {items.map((p) => (
            <option key={p.id_plato} value={p.id_plato}>
              {optLabel(p)}
            </option>
          ))}
        </select>
      </div>
    )
  }

  const monthTotalMargin = useMemo(() => {
    const keys = Object.keys(menuMap)
    return keys.reduce((acc, k) => acc + dayMargin(k), 0)
  }, [menuMap])

  return (
    <section className="section">
      <div className="container">
        <div className="level">
          <div className="level-left">
            <div>
              <h1 className="title is-4 mb-1">📅 Mantenedor Menú mensual (Admin)</h1>
              <p className="subtitle is-6">
                Define Fondo 1, Fondo 2, Hipocalórico, Vegetariano y Otro por día. (Con costos y precio para margen)
              </p>
            </div>
          </div>
          <div className="level-right" style={{gap: 10}}>
            <input className="input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
        </div>


        {err && <div className="notification is-danger is-light">{err}</div>}

        <div className="box">
          {loading ? (
            <div>⏳ Cargando...</div>
          ) : (
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 10}}>
              {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map((d) => (
                <div key={d} className="has-text-centered has-text-grey">{d}</div>
              ))}

              {gridCells.map((d, idx) => {
                if (!d) return <div key={idx} style={{height: 130}} />
                const dayYMD = ymd(d)
                const has = !!menuMap[dayYMD]
                const margin = dayMargin(dayYMD)
                return (
                  <div
                    key={idx}
                    className="box p-2"
                    style={{height: 130, cursor: 'pointer', background: has ? '#f6f6f6' : 'white'}}
                    onClick={() => openDay(dayYMD)}
                  >
                    <div className="is-flex is-justify-content-space-between is-align-items-center">
                      <b>{d.getDate()}</b>
                      <span className={`tag ${has ? 'is-success' : 'is-light'}`}>{has ? 'Definido' : 'Vacío'}</span>
                    </div>

                    <div className="mt-2" style={{fontSize: 12, color: '#666', lineHeight: 1.2}}>
                      {summary(dayYMD) || 'Sin opciones asignadas'}
                    </div>

                    <div className="mt-2" style={{fontSize: 12}}>
                      <span className="tag is-dark is-light">Margen: {formatCLP(margin)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Modal Bulma */}
        <div className={`modal ${show ? 'is-active' : ''}`}>
          <div className="modal-background" onClick={() => !saving && setShow(false)} />
          <div className="modal-card" style={{width: 820, maxWidth: '95%'}}>
            <header className="modal-card-head">
              <p className="modal-card-title">Menú del día: {fecha}</p>
              <button className="delete" aria-label="close" onClick={() => !saving && setShow(false)} />
            </header>

            <section className="modal-card-body">
              {err && <div className="notification is-danger is-light">{err}</div>}

              <div className="columns is-multiline">
                <div className="column is-12">
                  <label className="label">Fondo 1</label>
                  <SelectPlato value={f1} onChange={setF1} items={platosF1} />
                </div>

                <div className="column is-12">
                  <label className="label">Fondo 2</label>
                  <SelectPlato value={f2} onChange={setF2} items={platosF2} />
                </div>

                <div className="column is-12">
                  <label className="label">Hipocalórico</label>
                  <SelectPlato value={hipo} onChange={setHipo} items={platosHipo} />
                </div>

                <div className="column is-12">
                  <label className="label">Vegetariano</label>
                  <SelectPlato value={veg} onChange={setVeg} items={platosVeg} />
                </div>

                <div className="column is-12">
                  <label className="label">Otro</label>
                  <SelectPlato value={otro} onChange={setOtro} items={platosOtro} />
                </div>

                <div className="column is-12">
                  <label className="label">Nota (opcional)</label>
                  <input className="input" value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Ej: día de pescado" />
                </div>
              </div>

              <hr />

              <p className="has-text-weight-semibold mb-3">➕ Agregar plato (con costo y precio)</p>

              <div className="columns is-multiline is-variable is-2">
                <div className="column is-4">
                  <label className="label">Tipo</label>
                  <div className="select is-fullwidth">
                    <select value={newTipo} onChange={(e) => setNewTipo(e.target.value)}>
                      <option value="FONDO_1">{TYPE_LABEL.FONDO_1}</option>
                      <option value="FONDO_2">{TYPE_LABEL.FONDO_2}</option>
                      <option value="HIPOCALORICO">{TYPE_LABEL.HIPOCALORICO}</option>
                      <option value="VEGETARIANO">{TYPE_LABEL.VEGETARIANO}</option>
                      <option value="OTRO">{TYPE_LABEL.OTRO}</option>
                    </select>
                  </div>
                </div>

                <div className="column is-8">
                  <label className="label">Nombre del plato</label>
                  <input className="input" value={newPlato} onChange={(e) => setNewPlato(e.target.value)} placeholder="Ej: Pollo al jugo" />
                </div>

                <div className="column is-4">
                  <label className="label">Costo elaboración</label>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    step="1"
                    value={newCosto}
                    onChange={(e) => setNewCosto(e.target.value)}
                    placeholder="Ej: 2500"
                  />
                </div>

                <div className="column is-4">
                  <label className="label">Precio final</label>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    step="1"
                    value={newPrecio}
                    onChange={(e) => setNewPrecio(e.target.value)}
                    placeholder="Ej: 4500"
                  />
                </div>

                <div className="column is-4">
                  <label className="label">Margen</label>
                  <input
                    className="input"
                    value={formatCLP((Number(newPrecio || 0) || 0) - (Number(newCosto || 0) || 0))}
                    readOnly
                  />
                </div>

                <div className="column is-12">
                  <button
                    type="button"
                    className={`button is-link is-outlined ${saving ? 'is-loading' : ''}`}
                    onClick={addPlatoQuick}
                    disabled={saving}
                  >
                    Agregar plato al catálogo
                  </button>
                </div>
              </div>
            </section>

            <footer className="modal-card-foot is-justify-content-flex-end">
              <button className="button" onClick={() => !saving && setShow(false)} disabled={saving}>Cancelar</button>
              <button className={`button is-primary ${saving ? 'is-loading' : ''}`} onClick={saveDay} disabled={saving}>
                Guardar día
              </button>
            </footer>
          </div>
        </div>
      </div>
    </section>
  )
}
